import React, { useState } from 'react';

interface WarehouseStockManagerProps {
  storeId: string;
  totalStock: number;
  inventoryAllocation: Record<string, number>;
  onInventoryAllocationChange: (inventoryAllocation: Record<string, number>) => void;
}

export const WarehouseStockManager: React.FC<WarehouseStockManagerProps> = ({
  storeId,
  totalStock,
  inventoryAllocation,
  onInventoryAllocationChange,
}) => {
  const [expandedNonVariantStock, setExpandedNonVariantStock] = useState(false);

  // Mock warehouse data - in real app this would come from store/API
  const mockWarehouses = {
    "shopee_id": [
      { id: "A", name: "Warehouse A" },
      { id: "B", name: "Warehouse B" },
      { id: "C", name: "Warehouse C" },
    ],
    "lazada_id": [{ id: "A", name: "Warehouse A" }],
  };

  const storeWarehouses = mockWarehouses[storeId as keyof typeof mockWarehouses] || [
    { id: "MAIN", name: "Main Warehouse" },
  ];
  const hasMultipleWarehouses = storeWarehouses.length > 1;

  const handleWarehouseStockChange = (warehouseId: string, value: number) => {
    const updatedStock = {
      ...(inventoryAllocation || {}),
      [warehouseId]: value,
    };
    onInventoryAllocationChange(updatedStock);
  };

  if (!storeId) {
    return null;
  }

  return (
    <div>
      {/* Show expand button only when store has multiple warehouses */}
      {hasMultipleWarehouses && (
        <div className="tw-flex tw-items-center tw-gap-2 tw-mt-3">
          <button
            type="button"
            onClick={() => setExpandedNonVariantStock(!expandedNonVariantStock)}
            className="tw-text-blue-600 hover:tw-text-blue-700 tw-text-sm tw-font-medium tw-flex tw-items-center tw-gap-1"
          >
            <span className="material-symbols-outlined tw-text-base">
              {expandedNonVariantStock ? "expand_less" : "expand_more"}
            </span>
            {expandedNonVariantStock ? "Hide" : "Manage"} warehouse stock
            breakdown
          </button>
        </div>
      )}

      {/* Expanded warehouse stock inputs */}
      {hasMultipleWarehouses && expandedNonVariantStock && (
        <div className="tw-space-y-3 tw-pl-4 tw-border-l-2 tw-border-gray-200 tw-mt-3">
          <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-3 tw-gap-4">
            {storeWarehouses.map((warehouse) => (
              <div key={warehouse.id} className="tw-flex tw-flex-col tw-gap-2">
                <label className="tw-text-xs tw-font-medium tw-text-gray-500">
                  {warehouse.name}
                </label>
                <input
                  type="number"
                  className="tw-w-full tw-p-2 tw-text-sm tw-border tw-border-gray-300 tw-rounded-md focus:tw-ring-2 focus:tw-ring-blue-500 focus:tw-border-transparent"
                  placeholder="0"
                  min="0"
                  step="1"
                  value={inventoryAllocation?.[warehouse.id] || ""}
                  onChange={(e) =>
                    handleWarehouseStockChange(
                      warehouse.id,
                      parseInt(e.target.value) || 0
                    )
                  }
                />
              </div>
            ))}
          </div>

          {/* Stock summary */}
          <div className="tw-text-xs tw-text-gray-500 tw-pt-2">
            Total warehouse allocation:{" "}
            {Object.values(inventoryAllocation || {}).reduce((sum, val) => sum + val, 0)} /{" "}
            {totalStock} units
          </div>
        </div>
      )}
    </div>
  );
};